// ============================================================================
// Layout.js — Repeaked persistent shell (v2)
//
// Changes from v1:
//   • Right friends panel REMOVED entirely — friends now live in Dashboard
//   • Queue overlay is now a TRUE full-screen modal, not left-rail-only
//   • Custom Lobby nav item added
//   • Nav footer: ProfileCard with dropdown (View Profile, Settings, Log Out)
//
// Border rule: individual borderTop/Right/Bottom/Left everywhere — never
// the `border` shorthand mixed with individual sides.
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAdminAccess } from '../hooks/useAdminAccess';
import { BUG_REPORT_ORIGIN_KEY, isTrackableBugOrigin } from '../constants/bugReport';
import {
  QueueProvider,
  useQueue,
  MODE_5V5,
  MODE_WINGMAN,
  MAPS_5V5,
  MAPS_WINGMAN,
} from '../context/QueueContext';
import { setPartyMode } from '../api/api';

// ---------------------------------------------------------------------------
// Brand tokens
// ---------------------------------------------------------------------------
const C = {
  red:          '#E8192C',
  redDim:       '#a4111d',
  redGlow:      'rgba(232, 25, 44, 0.45)',
  orange:       '#FF6B2B',
  bg:           '#0C0905',
  bgWarm:       '#100B07',
  bgWarmer:     '#15100B',
  text:         '#F0F2F5',
  textDim:      '#9A938B',
  textVeryDim:  '#5C554E',
  card:         '#111111',
  cardAlt:      '#16110D',
  border:       '#1e1e1e',
  borderBright: '#2A2520',
  green:        '#3FCB7E',
};

const F = {
  display: '"Bebas Neue", "Oswald", Impact, sans-serif',
  body:    '"Rajdhani", "Inter", system-ui, -apple-system, sans-serif',
};

const NAV_COLLAPSED = 64;
const NAV_EXPANDED  = 228;
const BOTTOM_BAR_H  = 52;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
const useIsMobile = (bp = 960) => {
  const [m, setM] = useState(
    typeof window !== 'undefined' ? window.innerWidth < bp : false
  );
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return m;
};

const useHover = () => {
  const [h, setH] = useState(false);
  return [h, { onMouseEnter: () => setH(true), onMouseLeave: () => setH(false) }];
};

const useClickOutside = (ref, cb) => {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) cb(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, cb]);
};

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------
const formatTime = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------
const Diamond = ({ size = 16, color = C.red, dotColor = C.orange, withDot = false, hollow = true, style }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block', width: size, height: size,
      border: `${Math.max(1.5, size / 9)}px solid ${color}`,
      background: hollow ? 'transparent' : color,
      transform: 'rotate(45deg)', position: 'relative',
      boxSizing: 'border-box', flexShrink: 0, ...style,
    }}
  >
    {withDot && (
      <span style={{
        position: 'absolute', top: '50%', left: '50%',
        width: size * 0.32, height: size * 0.32, background: dotColor,
        transform: 'translate(-50%, -50%)',
      }} />
    )}
  </span>
);

const LogoLockup = ({ size = 20 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: size * 0.55 }}>
    <Diamond size={size} withDot />
    <span style={{ fontFamily: F.display, fontSize: size * 1.05, letterSpacing: 2, lineHeight: 1, color: C.text }}>
      RE<span style={{ color: C.red }}>PEAKED</span>
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Icons — flat 1.5px stroke, square caps
// ---------------------------------------------------------------------------
const Icon = ({ d, size = 22, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter"
    aria-hidden="true">
    {d}
  </svg>
);
const IconHome      = (p) => <Icon {...p} d={<><path d="M3 11 12 3l9 8"/><path d="M5 10v11h14V10"/></>} />;
const IconPlay      = (p) => <Icon {...p} d={<polygon points="6 4 20 12 6 20 6 4"/>} />;
const IconController= (p) => <Icon {...p} d={<><rect x="2" y="7" width="20" height="12" rx="0"/><path d="M7 11v4M9 13H5M17 11h2M15 13h2"/></>} />;
const IconMatches   = (p) => <Icon {...p} d={<><rect x="3" y="4" width="18" height="16"/><path d="M3 9h18M9 4v16"/></>} />;
const IconLeader    = (p) => <Icon {...p} d={<><path d="M6 21V11M12 21V4M18 21v-7"/></>} />;
const IconInventory = (p) => <Icon {...p} d={<><path d="M3 8h18v12H3z"/><path d="M8 8V4h8v4"/></>} />;
const IconProfile   = (p) => <Icon {...p} d={<><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7"/></>} />;
const IconSettings  = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></>} />;
const IconChevDown  = (p) => <Icon {...p} d={<path d="M6 9l6 6 6-6"/>} />;
const IconLogOut    = (p) => <Icon {...p} d={<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>} />;
const IconBug       = (p) => <Icon {...p} d={<><circle cx="12" cy="11.5" r="5"/><path d="M8 10 5 8"/><path d="M8 12 4 12"/><path d="M8 14 5 16"/><path d="M16 10 19 8"/><path d="M16 12 20 12"/><path d="M16 14 19 16"/></>} />;
const IconAdmin = ({ size = 22, color = 'currentColor' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.5"
    strokeLinecap="square"
    strokeLinejoin="miter"
    aria-hidden="true"
  >
    <path d="M12 2.5 5.5 5.5V11c0 4.5 2.9 8.3 6.5 9.2 3.6-.9 6.5-4.7 6.5-9.2V5.5L12 2.5z" />
    <path d="M12 10 14 12 12 14 10 12z" />
    <circle cx="12" cy="12" r="0.85" fill={color} stroke="none" />
  </svg>
);

// ===========================================================================
// DiamondFillTimer — bottom bar centrepiece
// ===========================================================================
const DiamondFillTimer = ({ size = 28, seconds = 0 }) => {
  const tau = 260;
  const fillPct = Math.min(0.98, 1 - Math.exp(-seconds / tau));
  return (
    <span style={{
      position: 'relative', display: 'inline-block',
      width: size, height: size, transform: 'rotate(45deg)',
      borderTop: `1.5px solid ${C.red}`, borderRight: `1.5px solid ${C.red}`,
      borderBottom: `1.5px solid ${C.red}`, borderLeft: `1.5px solid ${C.red}`,
      boxSizing: 'border-box', flexShrink: 0,
    }}>
      <span style={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(0deg, ${C.orange} 0%, rgba(255,107,43,0.85) 100%)`,
        clipPath: `inset(${(1 - fillPct) * 100}% 0 0 0)`,
        transition: 'clip-path 900ms cubic-bezier(0.2, 0.7, 0.2, 1)',
      }} />
      <span style={{
        position: 'absolute', top: '50%', left: '50%',
        width: size * 0.22, height: size * 0.22,
        background: fillPct > 0.5 ? C.text : C.orange,
        transform: 'translate(-50%, -50%)',
        transition: 'background 600ms ease',
      }} />
    </span>
  );
};

// ===========================================================================
// PROFILE CARD — nav footer with dropdown
// ===========================================================================
const ProfileCard = ({ expanded }) => {
  const { user, logoutUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  const username = user?.username || 'Player';
  const avatar   = user?.avatar_url;
  const initial  = username.slice(0, 1).toUpperCase();

  const items = [
    { label: 'View Profile', icon: <IconProfile size={15} />, action: () => { navigate(`/profile/${username}`); setOpen(false); } },
    { label: 'Settings',     icon: <IconSettings size={15} />, action: () => { navigate('/settings'); setOpen(false); } },
    { label: 'Log Out',      icon: <IconLogOut size={15} />,   action: () => { logoutUser?.(); navigate('/login'); setOpen(false); }, danger: true },
  ];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        aria-label="Account menu"
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: expanded ? '10px 14px' : '10px 0',
          justifyContent: expanded ? 'flex-start' : 'center',
          background: open ? C.cardAlt : 'transparent',
          borderTop: `1px solid ${C.border}`,
          borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
          cursor: 'pointer',
          transition: 'background 160ms ease',
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Avatar */}
        <div style={{
          width: 34, height: 34, flexShrink: 0,
          background: C.cardAlt,
          borderTop:    `1px solid ${C.border}`,
          borderRight:  `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`,
          borderLeft:   `2px solid ${C.red}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          marginLeft: expanded ? 0 : 'auto',
          marginRight: expanded ? 0 : 'auto',
        }}>
          {avatar
            ? <img src={avatar} alt={username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontFamily: F.display, fontSize: 16, color: C.text }}>{initial}</span>
          }
        </div>

        {/* Name + chevron */}
        <span style={{
          flex: expanded ? 1 : '0 0 0',
          width: expanded ? 'auto' : 0,
          minWidth: 0,
          opacity: expanded ? 1 : 0,
          overflow: 'hidden',
          transition: 'opacity 200ms ease',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <span style={{
            fontFamily: F.body, fontSize: 13, fontWeight: 700,
            color: C.text, letterSpacing: 0.5,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            flex: 1,
          }}>
            {username}
          </span>
          <span style={{
            color: C.textDim,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease',
            display: 'flex',
          }}>
            <IconChevDown size={14} />
          </span>
        </span>
      </button>

      {/* Dropdown — renders above, anchored to the button */}
      {open && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: expanded ? 8 : NAV_COLLAPSED / 2,
          transform: expanded ? 'none' : 'translateX(-50%)',
          minWidth: 200,
          background: C.card,
          borderTop:    `1px solid ${C.borderBright}`,
          borderRight:  `1px solid ${C.borderBright}`,
          borderBottom: `1px solid ${C.borderBright}`,
          borderLeft:   `2px solid ${C.red}`,
          zIndex: 200,
          marginBottom: 4,
          boxShadow: `0 8px 24px rgba(0,0,0,0.5)`,
        }}>
          {/* User info header */}
          <div style={{
            padding: '10px 14px',
            borderTop: 'none', borderRight: 'none', borderLeft: 'none',
            borderBottom: `1px solid ${C.border}`,
          }}>
            <div style={{ fontFamily: F.display, fontSize: 16, letterSpacing: 1.5, color: C.text }}>
              {username}
            </div>
            <div style={{ fontFamily: F.body, fontSize: 11, color: C.textDim, fontWeight: 600, letterSpacing: 1 }}>
              Repeaked account
            </div>
          </div>

          {/* Items */}
          {items.map(item => (
            <DropdownItem key={item.label} {...item} />
          ))}
        </div>
      )}
    </div>
  );
};

const DropdownItem = ({ label, icon, action, danger }) => {
  const [hover, hp] = useHover();
  return (
    <button
      {...hp}
      onClick={action}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px',
        background: hover ? (danger ? 'rgba(232,25,44,0.08)' : 'rgba(255,255,255,0.03)') : 'transparent',
        borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
        cursor: 'pointer', textAlign: 'left',
        color: danger ? (hover ? C.red : '#c44') : (hover ? C.text : C.textDim),
        transition: 'background 160ms ease, color 160ms ease',
      }}
    >
      {icon}
      <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 600, letterSpacing: 0.5 }}>
        {label}
      </span>
    </button>
  );
};

// ===========================================================================
// NAV RAIL
// ===========================================================================
const IconShop      = (p) => <Icon {...p} d={<><path d="M6 2L3 6v14h18V6l-3-4z"/><path d="M3 6h18M16 10a4 4 0 0 1-8 0"/></>} />;

const NAV_ITEMS = [
  { id: 'home',        label: 'Home',         path: '/dashboard',   Icon: IconHome       },
  { id: 'play',        label: 'Play',         path: null,           Icon: IconPlay,      isPlay: true },
  { id: 'custom',      label: 'Custom Match', path: '/custom',      Icon: IconController },
  { id: 'matches',     label: 'Matches',      path: '/matches',     Icon: IconMatches    },
  { id: 'leaderboard', label: 'Leaderboard',  path: '/leaderboard', Icon: IconLeader     },
  { id: 'inventory',   label: 'Inventory',    path: '/inventory',   Icon: IconInventory  },
  { id: 'shop',        label: 'Shop',         path: '/shop',        Icon: IconShop       },
];

const ADMIN_NAV_ITEM = {
  id: 'admin',
  label: 'Admin',
  path: '/admin',
  Icon: IconAdmin,
  accent: 'orange',
};

const REPORT_NAV_ITEM = {
  id: 'report-bug',
  label: 'Report Bug',
  path: '/report-bug',
  Icon: IconBug,
};

const NavItem = ({ item, expanded, active, onClick, pulseLabel }) => {
  const [hover, hp] = useHover();
  const showAccent = active || hover;
  const isOrange = item.accent === 'orange';
  const accentColor = isOrange ? C.orange : C.red;
  const accentGlow = isOrange ? 'rgba(255, 107, 43, 0.45)' : C.redGlow;
  const { Icon: I } = item;
  return (
    <button
      {...hp}
      onClick={onClick}
      aria-label={item.label}
      style={{
        position: 'relative', display: 'flex', alignItems: 'center',
        width: '100%', height: 50, padding: 0,
        background: active
          ? (isOrange ? 'rgba(255, 107, 43, 0.08)' : C.cardAlt)
          : hover
            ? (isOrange ? 'rgba(255, 107, 43, 0.05)' : 'rgba(255,255,255,0.02)')
            : 'transparent',
        borderTop:    'none', borderRight: 'none', borderBottom: 'none',
        borderLeft:   `3px solid ${active ? accentColor : hover && isOrange ? 'rgba(255, 107, 43, 0.35)' : 'transparent'}`,
        color: showAccent ? (isOrange && (active || hover) ? C.orange : C.text) : C.textDim,
        cursor: 'pointer',
        transition: 'background 160ms ease, color 160ms ease, border-left-color 160ms ease',
        textAlign: 'left',
      }}
    >
      <span style={{
        width: NAV_COLLAPSED - 3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        ...(pulseLabel ? { filter: `drop-shadow(0 0 6px ${accentGlow})` } : {}),
        ...(isOrange && (active || hover) ? { filter: `drop-shadow(0 0 5px ${accentGlow})` } : {}),
      }}>
        <I color={active || (isOrange && hover) ? accentColor : isOrange ? 'rgba(255, 107, 43, 0.72)' : 'currentColor'} />
      </span>
      <span style={{
        flex: 1, opacity: expanded ? 1 : 0, overflow: 'hidden', whiteSpace: 'nowrap',
        fontFamily: F.display, fontSize: 15, letterSpacing: 2, textTransform: 'uppercase',
        color: pulseLabel ? C.red : active ? accentColor : isOrange && hover ? C.orange : 'inherit',
        transition: 'opacity 200ms ease',
      }}>
        {pulseLabel || item.label}
      </span>
    </button>
  );
};

const NavRail = ({ expanded, setExpanded }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isAdmin } = useAdminAccess();
  const { queueActive, queueElapsed, openQueueOverlay } = useQueue();

  const handleNav = (item) => {
    if (item.isPlay) { openQueueOverlay(); return; }
    if (item.path === '/report-bug') {
      const originPath = `${location.pathname}${location.search}`;
      navigate(item.path, {
        state: {
          originPath: isTrackableBugOrigin(location.pathname) ? originPath : null,
        },
      });
      return;
    }
    if (item.path) navigate(item.path);
  };

  const isActive = (item) => {
    if (item.isPlay || !item.path) return false;
    return location.pathname === item.path ||
      (item.path !== '/' && location.pathname.startsWith(item.path));
  };

  return (
    <div
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      style={{
        position: 'fixed', top: 0, left: 0, bottom: 0,
        width: expanded ? NAV_EXPANDED : NAV_COLLAPSED,
        background: C.bgWarm,
        borderTop: 'none', borderBottom: 'none', borderLeft: 'none',
        borderRight: `1px solid ${C.border}`,
        transition: 'width 200ms cubic-bezier(0.2, 0.7, 0.2, 1)',
        zIndex: 50, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}
    >
      {/* Logo */}
      <button
        onClick={() => navigate('/')}
        aria-label="Repeaked home"
        style={{
          background: 'transparent', borderTop: 'none', borderRight: 'none', borderLeft: 'none',
          borderBottom: `1px solid ${C.border}`,
          height: 64, display: 'flex', alignItems: 'center',
          padding: '0 18px', cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        {expanded ? <LogoLockup size={18} /> : <Diamond size={22} withDot />}
      </button>

      {/* Nav items */}
      <nav style={{ flex: 1, paddingTop: 10, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {NAV_ITEMS.map(item => (
          <NavItem
            key={item.id} item={item} expanded={expanded}
            active={isActive(item)} onClick={() => handleNav(item)}
            pulseLabel={item.isPlay && queueActive ? formatTime(queueElapsed) : null}
          />
        ))}

        <div aria-hidden="true" style={{ flex: 1, minHeight: 32 }} />

        {user && isAdmin && (
          <NavItem
            item={ADMIN_NAV_ITEM}
            expanded={expanded}
            active={isActive(ADMIN_NAV_ITEM)}
            onClick={() => handleNav(ADMIN_NAV_ITEM)}
          />
        )}

        <NavItem
          item={REPORT_NAV_ITEM}
          expanded={expanded}
          active={isActive(REPORT_NAV_ITEM)}
          onClick={() => handleNav(REPORT_NAV_ITEM)}
        />
      </nav>

      {/* Profile Card footer */}
      <ProfileCard expanded={expanded} />
    </div>
  );
};

// ===========================================================================
// QUEUE OVERLAY — full-screen modal (not left-rail-only)
// ===========================================================================
const ModeCard = ({ active, disabled, onClick, title, subtitle, errorMsg }) => {
  const [hover, hp] = useHover();
  const accent = !disabled && (active || hover);
  return (
    <button
      {...hp} disabled={disabled} onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', gap: 4,
        width: '100%', padding: '16px 18px',
        background: active ? C.cardAlt : C.card,
        borderTop:    `1px solid ${errorMsg ? C.red : accent ? C.borderBright : C.border}`,
        borderRight:  `1px solid ${errorMsg ? C.red : accent ? C.borderBright : C.border}`,
        borderBottom: `1px solid ${errorMsg ? C.red : accent ? C.borderBright : C.border}`,
        borderLeft:   `2px solid ${errorMsg ? C.red : active ? C.red : accent ? C.borderBright : C.border}`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 160ms ease, border-color 160ms ease',
        textAlign: 'left', position: 'relative',
      }}
    >
      <span style={{ fontFamily: F.display, fontSize: 22, letterSpacing: 2, color: errorMsg ? C.red : active ? C.red : C.text, lineHeight: 1 }}>
        {title}
      </span>
      <span style={{ fontFamily: F.body, fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: errorMsg ? C.red : C.textDim }}>
        {errorMsg || subtitle}
      </span>
    </button>
  );
};

// MapTile — visual map card with background image
const MapTile = ({ map, selected, disabled, onToggle }) => {
  const [hover, hp] = useHover();
  return (
    <button
      {...hp} disabled={disabled} onClick={onToggle}
      style={{
        position: 'relative', height: 76,
        background: C.cardAlt,
        backgroundImage: map.img ? `url(/assets/maps/${map.img}.jpg)` : 'none',
        backgroundSize: 'cover', backgroundPosition: 'center',
        borderTop:    `1.5px solid ${selected ? C.red : hover ? C.borderBright : C.border}`,
        borderRight:  `1.5px solid ${selected ? C.red : hover ? C.borderBright : C.border}`,
        borderBottom: `1.5px solid ${selected ? C.red : hover ? C.borderBright : C.border}`,
        borderLeft:   `1.5px solid ${selected ? C.red : hover ? C.borderBright : C.border}`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        overflow: 'hidden', padding: 0,
        boxShadow: selected ? `0 0 0 1px ${C.red}55, 0 4px 12px -4px ${C.redGlow}` : 'none',
        transition: 'border-color 160ms ease, box-shadow 200ms ease, transform 160ms ease',
        transform: hover && !disabled ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      {/* Darkening gradient for legibility */}
      <span style={{
        position: 'absolute', inset: 0,
        background: selected
          ? `linear-gradient(0deg, rgba(12,9,5,0.85) 0%, rgba(12,9,5,0.25) 60%, rgba(12,9,5,0.5) 100%)`
          : `linear-gradient(0deg, rgba(12,9,5,0.92) 0%, rgba(12,9,5,0.55) 60%, rgba(12,9,5,0.75) 100%)`,
        transition: 'background 200ms ease',
      }} />

      {/* Selected check — top right */}
      {selected && (
        <span style={{
          position: 'absolute', top: 6, right: 6,
          width: 16, height: 16, background: C.red,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: F.display, fontSize: 11, color: C.text, lineHeight: 1,
        }}>
          ✓
        </span>
      )}

      {/* Map name — bottom */}
      <span style={{
        position: 'absolute', bottom: 6, left: 8, right: 8,
        fontFamily: F.display, fontSize: 14, letterSpacing: 1.5,
        color: selected ? C.text : C.textDim, textTransform: 'uppercase',
        textShadow: '0 1px 3px rgba(0,0,0,0.9)',
        textAlign: 'left', lineHeight: 1,
      }}>
        {map.name}
      </span>
    </button>
  );
};

// Compact party card for the queue overlay — horizontal row, smaller than dashboard
const QueuePartyCard = ({ member, isYou, isLeader, onEmptyClick }) => {
  if (!member) {
    return (
      <button
        onClick={onEmptyClick}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 6, padding: '10px 8px', flex: '1 1 0', minWidth: 0,
          background: 'transparent',
          borderTop:    `1px dashed ${C.borderBright}`,
          borderRight:  `1px dashed ${C.borderBright}`,
          borderBottom: `1px dashed ${C.borderBright}`,
          borderLeft:   `1px dashed ${C.borderBright}`,
          cursor: 'pointer', fontFamily: 'inherit',
          transition: 'border-color 160ms ease, background 160ms ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = C.orange; e.currentTarget.style.background = 'rgba(255,107,43,0.04)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = C.borderBright; e.currentTarget.style.background = 'transparent'; }}
      >
        <div style={{
          width: 36, height: 36,
          borderTop: `1px dashed ${C.borderBright}`, borderRight: `1px dashed ${C.borderBright}`,
          borderBottom: `1px dashed ${C.borderBright}`, borderLeft: `1px dashed ${C.borderBright}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontFamily: F.display, fontSize: 18, color: C.textVeryDim, lineHeight: 1 }}>+</span>
        </div>
        <span style={{ fontFamily: F.body, fontSize: 9, fontWeight: 700, letterSpacing: 1.5, color: C.textVeryDim, textTransform: 'uppercase' }}>
          Invite
        </span>
      </button>
    );
  }

  const username = member.username || 'Player';
  const initial  = username.slice(0, 1).toUpperCase();
  const accent   = isYou ? C.red : isLeader ? C.orange : C.borderBright;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 6, padding: '10px 8px', flex: '1 1 0', minWidth: 0,
      background: C.card,
      borderTop:    `1px solid ${C.border}`,
      borderRight:  `1px solid ${C.border}`,
      borderBottom: `2px solid ${accent}`,
      borderLeft:   `1px solid ${C.border}`,
      position: 'relative',
    }}>
      <div style={{
        width: 36, height: 36, background: C.cardAlt,
        borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        {member.avatar_url
          ? <img src={member.avatar_url} alt={username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ fontFamily: F.display, fontSize: 16, color: C.text }}>{initial}</span>
        }
      </div>
      <span style={{
        fontFamily: F.display, fontSize: 12, letterSpacing: 1, color: C.text, textTransform: 'uppercase',
        lineHeight: 1, maxWidth: '100%',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {username}
      </span>
      <span style={{
        fontFamily: F.body, fontSize: 8, fontWeight: 700, letterSpacing: 1.5,
        color: isYou ? C.red : isLeader ? C.orange : C.textDim, textTransform: 'uppercase',
        lineHeight: 1,
      }}>
        {isYou ? 'You' : isLeader ? 'Leader' : 'Member'}
      </span>
    </div>
  );
};

const QueueOverlay = () => {
  const {
    queueActive, queueLoading,
    startQueue, cancelQueue,
    closeQueueOverlay,
    mapPool5v5, mapPoolWingman, toggleMap,
    preferredMode, setPreferredMode,
    party: livePartyData,
    reloadParty,
  } = useQueue();
  const { user } = useAuth();

  const [mapsExpanded, setMapsExpanded] = useState(false);
  const [modeError, setModeError] = useState(null); // string | null

  const [modeChanging, setModeChanging] = useState(false);

  useEffect(() => {
    reloadParty?.();
  }, [reloadParty]);

  useEffect(() => {
    if (livePartyData?.mode) setPreferredMode(livePartyData.mode);
  }, [livePartyData?.mode, setPreferredMode]);

  // Auto-clear the mode error after 3.5s
  useEffect(() => {
    if (!modeError) return;
    const t = setTimeout(() => setModeError(null), 3500);
    return () => clearTimeout(t);
  }, [modeError]);

  // Party members from live data
  const partyMembers = livePartyData?.members || livePartyData?.players || [];
  const partySize    = Math.max(1, partyMembers.length);
  const leaderId     = livePartyData?.leader_id;
  const youAreLeader = !livePartyData || leaderId === (user?.id || user?.user_id);

  // Source of truth: party.mode from backend, fallback to preferredMode for solo
  const pendingMode = livePartyData?.mode || preferredMode;
  const disabled    = queueActive;

  const currentMaps     = pendingMode === MODE_WINGMAN ? MAPS_WINGMAN : MAPS_5V5;
  const currentSelected = pendingMode === MODE_WINGMAN ? mapPoolWingman : mapPool5v5;

  // Mode change — calls API if in a party, otherwise just updates local preference
  const tryChangeMode = async (newMode) => {
    if (disabled || modeChanging || !youAreLeader) return;
    if (newMode === MODE_WINGMAN && partySize > 2) {
      setModeError(`Too many players for Wingman (${partySize}/2). Kick members first.`);
      return;
    }
    setModeChanging(true);
    try {
      if (livePartyData) {
        await setPartyMode(newMode);
        await reloadParty?.();
      }
      setPreferredMode(newMode);
      setModeError(null);
    } catch (e) {
      setModeError(e?.response?.data?.detail || 'Failed to change mode');
    } finally {
      setModeChanging(false);
    }
  };

  const maxSlots = pendingMode === MODE_WINGMAN ? 2 : 5;

  // Build display slots: self first, then others, then empty slots
  const others = partyMembers.filter(m => (m.user_id || m.id) !== (user?.id || user?.user_id));
  const selfMember = {
    user_id: user?.id || user?.user_id,
    username: user?.username,
    avatar_url: user?.avatar_url,
  };
  const slots = [];
  slots.push({
    member: selfMember,
    isYou: true,
    isLeader: leaderId ? leaderId === selfMember.user_id : true,
  });
  for (let i = 0; i < maxSlots - 1; i++) {
    const m = others[i];
    slots.push({ member: m || null, isYou: false, isLeader: m && leaderId === (m.user_id || m.id) });
  }

  const handleFindMatch = async () => {
    if (!youAreLeader && !queueActive) return; // only leader can start queue
    if (queueActive) { await cancelQueue(); }
    else { try { await startQueue(pendingMode); } catch (_) {} }
  };

  // ESC closes overlay
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') closeQueueOverlay(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [closeQueueOverlay]);

  return (
    <div
      onClick={closeQueueOverlay}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(12,9,5,0.88)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 720,
          background: C.bgWarm,
          borderTop:    `1px solid ${C.border}`,
          borderRight:  `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`,
          borderLeft:   `2px solid ${C.red}`,
          display: 'flex', flexDirection: 'column',
          maxHeight: '90vh', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '20px 24px',
          borderTop: 'none', borderRight: 'none', borderLeft: 'none',
          borderBottom: `1px solid ${C.border}`,
        }}>
          <Diamond size={24} withDot color={C.red} dotColor={C.orange} />
          <div>
            <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.textDim }}>
              Matchmaking
            </div>
            <h2 style={{ fontFamily: F.display, fontSize: 38, letterSpacing: 2, color: C.text, margin: 0, lineHeight: 1, textTransform: 'uppercase' }}>
              Find a match<span style={{ color: C.red }}>.</span>
            </h2>
          </div>
          <button
            onClick={closeQueueOverlay}
            aria-label="Close"
            style={{
              marginLeft: 'auto', background: 'transparent',
              borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
              color: C.textDim, cursor: 'pointer', padding: 6, display: 'flex',
              transition: 'color 160ms ease',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = C.text)}
            onMouseLeave={e => (e.currentTarget.style.color = C.textDim)}
          >
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square">
              <path d="M6 6l12 12M18 6l-12 12"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* MODE */}
          <section>
            <SectionLabel>Mode</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
              <ModeCard
                active={pendingMode === MODE_5V5}
                disabled={disabled || modeChanging || !youAreLeader}
                onClick={() => tryChangeMode(MODE_5V5)}
                title="5v5 Competitive"
                subtitle="MR15 · 30 rounds"
              />
              <ModeCard
                active={pendingMode === MODE_WINGMAN}
                disabled={disabled || modeChanging || !youAreLeader}
                onClick={() => tryChangeMode(MODE_WINGMAN)}
                title="Wingman"
                subtitle="MR8 · 16 rounds"
              />
            </div>

            {/* Mode error message */}
            {modeError && (
              <div style={{
                marginTop: 10, padding: '10px 14px',
                background: 'rgba(232,25,44,0.08)',
                borderTop:    `1px solid ${C.red}`,
                borderRight:  `1px solid ${C.red}`,
                borderBottom: `1px solid ${C.red}`,
                borderLeft:   `2px solid ${C.red}`,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <Diamond size={6} color={C.red} hollow={false} />
                <span style={{
                  fontFamily: F.body, fontSize: 12, fontWeight: 600,
                  letterSpacing: 0.5, color: C.text, lineHeight: 1.4,
                }}>
                  {modeError}
                </span>
              </div>
            )}
          </section>

          {/* MAP POOL */}
          <section>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <SectionLabel>Map pool</SectionLabel>
            </div>
            <button
              onClick={() => (youAreLeader || !livePartyData) && setMapsExpanded(v => !v)}
              style={{
                width: '100%', marginTop: 10,
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 16px',
                background: C.card,
                borderTop:    `1px solid ${mapsExpanded ? C.borderBright : C.border}`,
                borderRight:  `1px solid ${mapsExpanded ? C.borderBright : C.border}`,
                borderBottom: `1px solid ${mapsExpanded ? C.borderBright : C.border}`,
                borderLeft:   `2px solid ${C.orange}`,
                cursor: 'pointer',
                fontFamily: F.display, fontSize: 15, letterSpacing: 1.5,
                color: C.text, textTransform: 'uppercase', textAlign: 'left',
                transition: 'border-color 160ms ease',
              }}
            >
              <span style={{ flex: 1 }}>
                Maps <span style={{ color: C.orange }}>({currentSelected.length}/{currentMaps.length})</span>
              </span>
              <span style={{
                fontFamily: F.body, fontSize: 11, fontWeight: 600,
                letterSpacing: 1.5, color: C.textDim, textTransform: 'uppercase',
              }}>
                {mapsExpanded ? 'Hide' : 'Edit'}
              </span>
              <span style={{
                display: 'inline-block', color: C.textDim,
                transform: mapsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 200ms ease',
                lineHeight: 1,
              }}>
                ▾
              </span>
            </button>

            {mapsExpanded && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6,
                marginTop: 10,
              }}>
                {currentMaps.map(m => (
                  <MapTile
                    key={m.id}
                    map={m}
                    selected={currentSelected.includes(m.id)}
                    disabled={disabled}
                    onToggle={() => youAreLeader && !disabled && toggleMap(pendingMode, m.id)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* PARTY */}
          <section>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <SectionLabel>Party</SectionLabel>
              <span style={{ fontFamily: F.body, fontSize: 11, color: C.textVeryDim, fontWeight: 600, marginLeft: 'auto', letterSpacing: 1.5, textTransform: 'uppercase' }}>
                {partySize} / {maxSlots}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              {slots.map((s, i) => (
                <QueuePartyCard
                  key={i}
                  member={s.member}
                  isYou={s.isYou}
                  isLeader={s.isLeader}
                  onEmptyClick={() => {
                    closeQueueOverlay();
                    window.dispatchEvent(new CustomEvent('repeaked:open-friends'));
                  }}
                />
              ))}
            </div>
          </section>
        </div>

        {/* CTA — full width at bottom */}
        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${C.border}`,
          borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
        }}>
          <QueueCTAButton active={queueActive} loading={queueLoading} onClick={handleFindMatch} disabled={!youAreLeader && !queueActive} notLeader={!youAreLeader && !queueActive} />
        </div>
      </div>
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 8,
    fontFamily: F.body, fontSize: 10, fontWeight: 700,
    letterSpacing: 2.5, textTransform: 'uppercase', color: C.textDim,
  }}>
    <Diamond size={6} color={C.red} hollow={false} />
    {children}
  </div>
);

const QueueCTAButton = ({ active, loading, onClick, notLeader }) => {
  const [hover, hp] = useHover();
  const isDisabled = loading || notLeader;
  return (
    <button
      {...hp} onClick={onClick} disabled={isDisabled}
      title={notLeader ? 'Only the party leader can start the queue' : undefined}
      style={{
        width: '100%', padding: '14px 20px',
        background: notLeader ? C.card : active ? 'transparent' : (loading ? C.redDim : C.red),
        color: notLeader ? C.textDim : active ? C.red : C.text,
        borderTop:    `1.5px solid ${notLeader ? C.border : active ? C.red : 'transparent'}`,
        borderRight:  `1.5px solid ${notLeader ? C.border : active ? C.red : 'transparent'}`,
        borderBottom: `1.5px solid ${notLeader ? C.border : active ? C.red : 'transparent'}`,
        borderLeft:   `1.5px solid ${notLeader ? C.border : active ? C.red : 'transparent'}`,
        borderRadius: 2,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        fontFamily: F.display, fontSize: 17, letterSpacing: 2.5, textTransform: 'uppercase',
        opacity: notLeader ? 0.5 : 1,
        transform: hover && !isDisabled ? 'translateY(-1px)' : 'translateY(0)',
        boxShadow: !active && !notLeader && hover && !loading ? `0 8px 20px -8px ${C.redGlow}, 0 0 0 1px ${C.orange}33` : !active && !notLeader ? `0 4px 12px -6px ${C.redGlow}` : 'none',
        transition: 'transform 160ms ease, box-shadow 200ms ease, background 160ms ease',
      }}
    >
      {loading ? '…' : notLeader ? 'Only leader can queue' : active ? 'Cancel queue' : 'Find match →'}
    </button>
  );
};


const QueueBottomBar = ({ navOffset }) => {
  const { queueActive, queueMode, queueElapsed, cancelQueue, queueLoading, activeMapPool, activeMapList, openQueueOverlay } = useQueue();
  const [cancelHover, ch] = useHover();

  if (!queueActive) return null;

  const modeLabel = queueMode === MODE_WINGMAN ? 'Wingman' : '5v5 Competitive';

  return (
    <div style={{
      position: 'fixed', left: navOffset, right: 0, bottom: 0,
      height: BOTTOM_BAR_H, background: C.card,
      borderTop: `1px solid ${C.red}`,
      borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
      display: 'flex', alignItems: 'center',
      padding: '0 20px', gap: 18, zIndex: 40,
      transition: 'left 200ms cubic-bezier(0.2, 0.7, 0.2, 1)',
    }}>
      <button
        onClick={openQueueOverlay}
        style={{ background: 'transparent', borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none', cursor: 'pointer', display: 'flex' }}
      >
        <DiamondFillTimer size={28} seconds={queueElapsed} />
      </button>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span style={{ fontFamily: F.display, fontSize: 15, letterSpacing: 2, color: C.text, textTransform: 'uppercase', lineHeight: 1 }}>
          {modeLabel}
        </span>
        <span style={{ fontFamily: F.body, fontSize: 11, fontWeight: 600, letterSpacing: 1, color: C.textDim, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          {activeMapPool?.length || 0} of {activeMapList?.length || 0} maps
        </span>
      </div>

      <div style={{ flex: 1 }} />

      <div style={{ fontFamily: F.display, fontSize: 22, letterSpacing: 2, color: C.text, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {formatTime(queueElapsed)}
      </div>

      <button
        {...ch} onClick={cancelQueue} disabled={queueLoading}
        style={{
          background: cancelHover ? 'rgba(232,25,44,0.10)' : 'transparent',
          color: C.red,
          borderTop:    `1.5px solid ${C.red}`,
          borderRight:  `1.5px solid ${C.red}`,
          borderBottom: `1.5px solid ${C.red}`,
          borderLeft:   `1.5px solid ${C.red}`,
          padding: '6px 14px', cursor: queueLoading ? 'wait' : 'pointer',
          fontFamily: F.display, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase',
          transition: 'background 160ms ease',
        }}
      >
        Cancel
      </button>
    </div>
  );
};

// ===========================================================================
// MATCH FOUND MODAL
// ===========================================================================
const MatchFoundModal = () => {
  const { matchFound, dismissMatchFound } = useQueue();
  const navigate = useNavigate();
  if (!matchFound) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(12,9,5,0.92)',
      backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
      zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{
        width: '100%', maxWidth: 480,
        background: C.card,
        borderTop:    `1px solid ${C.border}`,
        borderRight:  `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`,
        borderLeft:   `3px solid ${C.red}`,
        padding: 32, textAlign: 'center',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <Diamond size={36} withDot color={C.red} dotColor={C.orange} />
        </div>
        <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.textDim, marginBottom: 8 }}>
          Stand by
        </div>
        <h2 style={{ fontFamily: F.display, fontSize: 56, lineHeight: 0.95, letterSpacing: 1.5, color: C.text, margin: '0 0 18px 0' }}>
          Match <span style={{ color: C.red }}>found.</span>
        </h2>
        <p style={{ fontFamily: F.body, fontSize: 14, lineHeight: 1.5, color: C.textDim, margin: '0 0 24px 0' }}>
          Your match is ready. Accept to lock in.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button
            onClick={() => { dismissMatchFound(); if (matchFound.matchId) navigate(`/match/${matchFound.matchId}`); }}
            style={{
              background: C.red, color: C.text,
              borderTop: 'none', borderRight: 'none', borderBottom: 'none', borderLeft: 'none',
              padding: '12px 28px', cursor: 'pointer',
              fontFamily: F.display, fontSize: 15, letterSpacing: 2.5, textTransform: 'uppercase',
              boxShadow: `0 8px 20px -8px ${C.redGlow}`,
            }}
          >
            Accept →
          </button>
          <button
            onClick={dismissMatchFound}
            style={{
              background: 'transparent', color: C.textDim,
              borderTop:    `1.5px solid ${C.borderBright}`,
              borderRight:  `1.5px solid ${C.borderBright}`,
              borderBottom: `1.5px solid ${C.borderBright}`,
              borderLeft:   `1.5px solid ${C.borderBright}`,
              padding: '12px 24px', cursor: 'pointer',
              fontFamily: F.display, fontSize: 14, letterSpacing: 2, textTransform: 'uppercase',
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

// ===========================================================================
// LAYOUT INNER
// ===========================================================================
function LayoutInner({ children }) {
  const isMobile = useIsMobile(960);
  const [navExpanded, setNavExpanded] = useState(false);
  const { queueActive, queueOverlayOpen } = useQueue();
  const location = useLocation();

  useEffect(() => {
    const originPath = `${location.pathname}${location.search}`;
    if (isTrackableBugOrigin(location.pathname)) {
      sessionStorage.setItem(BUG_REPORT_ORIGIN_KEY, originPath);
    }
  }, [location]);

  const navWidth = navExpanded ? NAV_EXPANDED : NAV_COLLAPSED;

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: F.body, minHeight: '100vh', position: 'relative' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Rajdhani:wght@400;500;600;700&display=swap');
        html, body { background: ${C.bg}; margin: 0; padding: 0; }
        * { box-sizing: border-box; }
        ::selection { background: ${C.red}; color: ${C.text}; }
        a:focus-visible, button:focus-visible { outline: 2px solid ${C.orange}; outline-offset: 3px; }
        input::placeholder { color: ${C.textVeryDim}; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: ${C.bg}; }
        ::-webkit-scrollbar-thumb { background: ${C.borderBright}; }
        ::-webkit-scrollbar-thumb:hover { background: ${C.red}; }
        @keyframes repeakedPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
      `}</style>

      {/* Left nav rail */}
      {!isMobile && <NavRail expanded={navExpanded} setExpanded={setNavExpanded} />}

      {/* Mobile top bar */}
      {isMobile && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 50, height: 56,
          background: C.bgWarm, borderTop: 'none', borderRight: 'none', borderLeft: 'none',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12,
        }}>
          <Diamond size={22} withDot />
          <span style={{ fontFamily: F.display, fontSize: 16, letterSpacing: 2, color: C.text }}>
            RE<span style={{ color: C.red }}>PEAKED</span>
          </span>
        </div>
      )}

      {/* Main content — no right margin anymore */}
      <main style={{
        marginLeft: isMobile ? 0 : navWidth,
        marginRight: 0,
        paddingBottom: queueActive ? BOTTOM_BAR_H + 16 : 16,
        minHeight: '100vh',
        overflowY: 'visible',
        transition: 'margin-left 200ms cubic-bezier(0.2, 0.7, 0.2, 1)',
      }}>
        {children ?? <Outlet />}
      </main>

      {/* Queue overlay — full screen */}
      {queueOverlayOpen && <QueueOverlay />}

      {/* Queue bottom bar */}
      <QueueBottomBar navOffset={isMobile ? 0 : navWidth} />

      {/* Match found modal */}
      <MatchFoundModal />
    </div>
  );
}

// ===========================================================================
// EXPORTED LAYOUT
// ===========================================================================
export default function Layout({ children }) {
  return (
    <QueueProvider>
      <LayoutInner>{children}</LayoutInner>
    </QueueProvider>
  );
}